<template>
  <div class="action-buttons">
    <button class="btn-secondary" @click="handleBack" :disabled="isLoading">
      <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
        <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
      </svg>
      Back
    </button>
    
    <button class="btn-primary" @click="handleFinish" :disabled="isLoading || hasInlineScreening">
      <div v-if="isLoading" class="loading-spinner"></div>
      <svg v-else class="btn-icon" width="20" height="20" viewBox="0 0 24 24">
        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
      </svg>
      {{ finishButtonText }}
    </button>
  </div>
</template>

<script>
export default {
  name: 'SurveyActionButtons',
  
  props: {
    isLoading: {
      type: Boolean,
      default: false
    },
    hasInlineScreening: {
      type: Boolean,
      default: false
    },
    pendingScreeningSystemType: {
      type: Number,
      default: null
    }
  },

  computed: {
    finishButtonText() {
      if (this.isLoading) {
        return 'Loading...';
      }
      
      if (this.hasInlineScreening) {
        return 'Complete Screening First';
      }
      
      return 'Finish Assessment';
    }
  },

  methods: {
    handleBack() {
      const message = "Are you sure you want to go back? This will allow you to edit the current assessment.\n\nIf you would like to start a new case assessment, please select 'Finish Assessment'.";
      if (confirm(message)) {
        this.$emit('back-clicked');
      }
    },

    handleFinish() {
      if (this.isLoading || this.hasInlineScreening) {
        return;
      }
      
      this.$emit('finish-clicked');
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../breakpoints.scss";

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #E5E7EB;

  @include md {
    flex-direction: row;
    justify-content: space-between;
  }
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;
  width: 100%;
  position: relative;

  @include md {
    width: auto;
    min-width: 160px;
  }

  .btn-icon {
    flex-shrink: 0;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none !important;
    box-shadow: none !important;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  box-shadow: 0 2px 6px rgba(1, 59, 130, 0.2);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(1, 59, 130, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);
  }

  &:disabled {
    background: #9CA3AF;
    box-shadow: none;
  }
}

.btn-secondary {
  background: white;
  color: #000001;
  border: 1px solid #D1D5DB;

  &:hover:not(:disabled) {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active:not(:disabled) {
    background: #F3F4F6;
  }

  &:disabled {
    background: #F3F4F6;
    color: #9CA3AF;
    border-color: #E5E7EB;
  }
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

// Special styling for when inline screening is present
.action-buttons:has(.btn-primary:disabled) {
  .btn-primary {
    background: #F59E0B;
    color: white;
    
    &:hover {
      background: #D97706;
    }
  }
}

// Add a subtle hint when screening is pending
@keyframes pulseWarning {
  0%, 100% {
    box-shadow: 0 2px 6px rgba(245, 158, 11, 0.2);
  }
  50% {
    box-shadow: 0 2px 6px rgba(245, 158, 11, 0.4);
  }
}

.btn-primary:disabled {
  animation: pulseWarning 2s ease-in-out infinite;
}
</style>